import { useState } from "react";
import { createFileRoute, useLocation, useNavigate } from "@tanstack/react-router";
import { moduleTabOf, replaceWithBareModule } from "@/app/module-address";
import { tabSearch } from "@/app/tabs";
import { PendingSurface } from "@/components/feedback/pending-surface";
import { useEditOutlet, useUnsavedGuard } from "@/components/kit/unsaved-guard";
import {
  SettingsLeaveContext,
  SettingsTabs,
  settingsSections,
  visibleSettingsSections,
} from "@/features/settings";
import { requirePerm } from "@/lib/auth/guards";

export const Route = createFileRoute("/_authenticated/settings")({
  // An old `?tab=` still names a section. It is read once, then the address
  // is replaced by `/settings` (`app/module-address.ts`).
  validateSearch: tabSearch("settings"),
  beforeLoad: ({ context, search, location }) => {
    replaceWithBareModule(
      "/settings",
      location,
      typeof search.tab === "string" ? { moduleTab: search.tab } : undefined,
    );
    requirePerm("EDIT_STAFF")({ context });
  },
  component: SettingsRoute,
});

/**
 * The tab strip over the section in memory. The strip is always drawn.
 * The address is `/settings` and nothing after it (Rachel, 22-10-2026).
 *
 * A FRESH ADDRESS IS THE FIRST TAB (Rachel, 28-09-2026): the nav link carries
 * no section, and clicking Settings from anywhere else lands on Pharmacy &
 * branches. An id the registry does not hold still keeps the pending surface
 * behind a strip that names every section.
 *
 * The access log is the `log` section (`features/settings/access-log-tab.tsx`).
 */
function SettingsRoute() {
  const seeded = useLocation({ select: (location) => moduleTabOf(location.state) });
  const [chosen, setChosen] = useState(seeded);
  // A later arrival (an old `?tab=` replaced onto this entry) names the section.
  // A press below writes the same state, so the two stay one value. Adjusting
  // during render keeps the section on the first paint of that arrival.
  const [adopted, setAdopted] = useState(seeded);
  if (seeded !== adopted) {
    setAdopted(seeded);
    setChosen(seeded);
  }

  const navigate = useNavigate();
  const { session } = Route.useRouteContext();
  const outlet = useEditOutlet();
  const guard = useUnsavedGuard(outlet.edit);
  const all = settingsSections.use();
  const sections = visibleSettingsSections(all, session);
  const section =
    sections.find((item) => item.id === chosen) ??
    (chosen === undefined || all.some((item) => item.id === chosen) ? sections[0] : undefined);

  const select = (id: string) => {
    guard.guarded(() => {
      setChosen(id);
      // The address stays `/settings`. The section rides the history entry so
      // a refresh of this tab keeps it; a copied address does not.
      void navigate({
        to: "/settings",
        search: {},
        hash: "",
        state: { moduleTab: id },
        replace: true,
        ignoreBlocker: true,
      });
    });
  };

  return (
    <SettingsLeaveContext.Provider value={outlet.report}>
      <div className="flex min-h-0 flex-1 flex-col">
        <SettingsTabs active={section?.id ?? ""} session={session} onSelect={select} />
        {section ? <section.Component /> : <PendingSurface titleKey="nav.settings" />}
        {guard.dialogue}
      </div>
    </SettingsLeaveContext.Provider>
  );
}
